import { z } from 'zod';
import { entrySource, id, isoDateTime, likert5, localDate, percent } from './common.js';
/**
 * Fast emotional check-in. The whole point is that it takes under fifteen
 * seconds — a check-in that feels like a form is a check-in that stops
 * happening by week two. Every field except `mood` is optional.
 *
 * Dimensions are chosen for ADHD specifically rather than generic mood
 * tracking: focus, restlessness, overwhelm and rejection sensitivity are the
 * axes that actually move day to day and that medication visibly affects.
 */
export const moodScale = z.number().int().min(-5).max(5);
/**
 * The slider's second thought (13 Oct 2026). A release within SLIDER_UPDATE_MINUTES
 * of the latest check-in UPDATES that check-in's mood instead of adding one:
 * four releases in ten seconds were four rows, which is a bump, not four
 * check-ins. The row keeps its id; its time moves to the latest word.
 */
export const SLIDER_UPDATE_MINUTES = 15;
/**
 * A revision of a recent check-in, today only. The slider sends `mood`; the
 * fuller form under it (14 Oct 2026) sends the note, the tags and whichever
 * ratings were set, so "Add a note" adds to the check-in the slider just made
 * instead of making a second one. A field left out is left alone.
 */
export const updateMoodCheckin = z
    .object({
    mood: moodScale.optional(),
    energy: likert5.nullable().optional(),
    focus: likert5.nullable().optional(),
    restlessness: likert5.nullable().optional(),
    overwhelm: likert5.nullable().optional(),
    irritability: likert5.nullable().optional(),
    rejectionSensitivity: likert5.nullable().optional(),
    anxiety: likert5.nullable().optional(),
    contextTags: z.array(z.string().min(1).max(40)).max(20).optional(),
    note: z.string().max(4000).nullable().optional(),
})
    .strict();
export const createMoodCheckin = z.object({
    recordedAt: isoDateTime.optional(),
    /**
     * Which day this belongs to, when that is not the day it was given.
     *
     * Only ever set by the mood offer on a journal entry that was itself filed to
     * yesterday under the late-night grace, so the feeling and the writing land on
     * the same day. The server accepts today or yesterday and nothing else.
     */
    day: localDate.optional(),
    /** -5 (awful) .. 0 (neutral) .. +5 (great). */
    mood: moodScale,
    energy: likert5.nullable().default(null),
    focus: likert5.nullable().default(null),
    restlessness: likert5.nullable().default(null),
    overwhelm: likert5.nullable().default(null),
    irritability: likert5.nullable().default(null),
    /** Rejection-sensitive dysphoria — a big one for ADHD, poorly served by generic trackers. */
    rejectionSensitivity: likert5.nullable().default(null),
    anxiety: likert5.nullable().default(null),
    /** Free-form labels: 'work', 'social', 'post-meeting', 'no sleep'. */
    contextTags: z.array(z.string().min(1).max(40)).max(20).default([]),
    emotions: z
        .array(z.object({
        emotion: z.string().min(1).max(40),
        intensity: percent,
    }))
        .max(12)
        .default([]),
    note: z.string().max(4000).nullable().default(null),
    source: entrySource.default('manual'),
    /**
     * The entry this came out of, when it was confirmed from one.
     *
     * Makes the two rows one moment rather than two — the same pairing
     * thought_records.voice_log_id already gives a record drafted from an entry.
     */
    voiceLogId: id.nullable().default(null),
});
/*
 * THE EMOTION WHEEL WAS HERE, and is deleted.
 *
 * Thirty-six curated words across anger, fear, sadness and the rest, with a
 * comment citing the affect-labelling literature: naming a feeling precisely
 * is itself a regulation step, so the picker offered real words instead of
 * five smileys. The argument was good.
 *
 * It was never built. The constant was served at GET /mood/emotions and
 * wrapped as api.emotionWheel, and that wrapper had NO CALLER anywhere in
 * packages/web — there was no control on any screen to tag an emotion with.
 * So "0 of 67 check-ins carry one" measured an unbuilt feature and said
 * nothing whatever about whether he wanted it.
 *
 * The need it was for is met by the note, which he uses on 27 of 67. And a
 * column the UI never fills is exactly the shape of Sos.tsx:83, where
 * distress_before has been null on all fourteen Help now events since the
 * feature shipped.
 *
 * `mood_checkin_emotions` stays in the database at zero rows until its own
 * migration: dropping a table is the one thing here that cannot be undone by
 * reverting a commit.
 */
/**
 * The things a day can be marked with.
 *
 * Fixed, and short. Chosen so that each one is a FACT he can answer in a second
 * at 11pm rather than a judgement he has to make — "short night", not "slept
 * badly"; "no fixed plans", not "unproductive". Every entry is an antecedent,
 * something that happened to or around the day, never an outcome: the check-in
 * already rates eight feelings, and a marker that records a feeling is a worse
 * copy of better data. An antecedent also cannot be failed, which is what keeps
 * this list off being a scorecard.
 *
 * All eight are shown; there is no picker. A screen for choosing which of eight
 * to display is more machinery than the choice is worth.
 */
export const DAY_MARKS = [
    { kind: 'saw_people', label: 'Saw people' },
    { kind: 'short_night', label: 'Short night' },
    { kind: 'exercise', label: 'Exercise' },
    { kind: 'sex', label: 'Sex' },
    { kind: 'alcohol', label: 'Alcohol' },
    { kind: 'unwell', label: 'Unwell or in pain' },
    { kind: 'no_plans', label: 'No fixed plans' },
    { kind: 'argument', label: 'Argument' },
];
export const dayMarkKind = z.enum(DAY_MARKS.map((m) => m.kind));
export const setDayMark = z.object({
    day: localDate,
    kind: dayMarkKind,
});
/* =============================================================================
 * What a mood number is CALLED.
 *
 * This lived in packages/web/src/lib/mood.ts, which is reachable from every
 * screen he opens and from none of the pages he shares. So the check-in screen
 * has shown him the word under his thumb since August — `Rough · −3`,
 * CheckIn.tsx:182 — and a recipient has only ever been given the integer.
 * SharedView.tsx:10 imports `whenWritten` and `writtenLate` from that exact
 * file and leaves `moodLabel` behind.
 *
 * It is here because this repo's rule is that a predicate the UI must announce
 * belongs in @adhd/shared and gets imported by both sides. The shared page's
 * note and the public manual both described this scale in prose, which made
 * them the third and fourth statements of it; they now render MOOD_WORDS.
 *
 * ONE SOURCE, and it has to be the CHAIN rather than the bounds. The two are
 * not the same thing, which is the trap:
 *
 *   moodLabel(-0.5) is "A bit low"   — the band below zero is open at its top
 *   moodLabel(+0.5) is "Alright"     — but Neutral is EXACTLY zero
 *
 * so a table of integer bounds, read with `mood <= hi`, silently renames every
 * fractional value between −1 and 0. Day figures on the shared page are means
 * (DayStrip.tsx:132) and nine of his 33 logged days carry one, so that rename
 * would have been visible on the page this change exists to fix. The chain is
 * therefore the authority and the integer bounds are DERIVED from it.
 * ========================================================================== */
/**
 * The bands, in the order they are tested, exactly as the original chain of
 * ifs tested them. `exclusive` is the one that reads `mood < upTo` — it is what
 * keeps "Neutral" meaning precisely zero, which is deliberate: rounding a zero
 * into "Alright" would put a word in somebody's mouth.
 */
const MOOD_BANDS = [
    { word: 'Awful', upTo: -4, exclusive: false },
    { word: 'Rough', upTo: -2, exclusive: false },
    { word: 'A bit low', upTo: 0, exclusive: true },
    { word: 'Neutral', upTo: 0, exclusive: false },
    { word: 'Alright', upTo: 2, exclusive: false },
    { word: 'Good', upTo: 4, exclusive: false },
    { word: 'Great', upTo: Infinity, exclusive: false },
];
/**
 * What a mood number is called. Takes a mean as happily as a rating.
 *
 * The bands are deliberately uneven — four of the seven words cover two scale
 * points and three cover one. That is a fact about the instrument and it has a
 * consequence for anything that DRAWS these: seven equal bars make the wide
 * bands look twice as tall for free, which is how a flat distribution came to
 * be read as two groups with a thin middle. Draw the eleven points, or divide
 * by `width`. Never seven equal bars.
 */
export function moodLabel(mood) {
    for (const b of MOOD_BANDS) {
        if (b.exclusive ? mood < b.upTo : mood <= b.upTo)
            return b.word;
    }
    /* Unreachable: the last band's bound is Infinity. Here so the function has a
       return type of MoodWord rather than MoodWord | undefined. */
    return 'Great';
}
/**
 * The seven words with the INTEGER ratings each one covers, for a legend.
 *
 * Derived from the chain above rather than written out beside it, so that
 * changing a band moves the legend with it. `width` is how many scale points
 * the word spans, and it is exported because a chart that ignores it lies.
 */
export const MOOD_WORDS = (() => {
    const out = [];
    for (let n = -5; n <= 5; n += 1) {
        const word = moodLabel(n);
        const last = out[out.length - 1];
        if (last && last.word === word) {
            last.hi = n;
            last.width += 1;
        }
        else {
            out.push({ word, lo: n, hi: n, width: 1 });
        }
    }
    return out;
})();
/** The ends of the scale, in words, for anywhere that needs to name them. */
export const MOOD_MIN = -5;
export const MOOD_MAX = 5;
/**
 * Three colours, not seven — and derived from the WORD, not the number.
 *
 * A seven-step colour scale would read as a severity chart of somebody's week,
 * which is a thing to look at on purpose in Trends rather than something a list
 * does to them while they scroll.
 *
 * It banded the number independently once, and the two sets of boundaries did
 * not line up: "Alright" covers +1 and +2, the colour split at "> 1", so one
 * day averaging +1.3 and the next averaging +1.7 both printed "Alright", one in
 * the middling colour and one in the good one, three centimetres apart on the
 * same screen — the app disagreeing with itself about a word it had chosen.
 * Deriving the colour from the label makes the two structurally incapable of
 * disagreeing.
 *
 * It returns a CSS custom property, which is why this is the one thing in
 * @adhd/shared that knows a stylesheet exists. It stays beside `moodLabel`
 * anyway: splitting them across packages is how they came to disagree before.
 * The server never calls it.
 */
const LOW = new Set(['Awful', 'Rough']);
const HIGH = new Set(['Good', 'Great']);
export function moodColor(mood) {
    const word = moodLabel(mood);
    if (LOW.has(word))
        return 'var(--mood-low)';
    if (HIGH.has(word))
        return 'var(--mood-high)';
    return 'var(--mood-mid)';
}
/**
 * "Rough −3" — the word and the figure, which is the whole point of release 1.
 *
 * One definition, because it appears on the day cards, the Today facts and the
 * day fold, and three hand-written variants of `${moodLabel(n)} ${signed(n)}`
 * is the state this app's recurring defect starts in.
 *
 * `digits` matches the caller's own rounding so the word and the number cannot
 * describe different values.
 */
/**
 * A signed figure, to a stated number of digits.
 *
 * ONE COPY. There were two, and they disagreed: DayStrip.tsx:187 was
 * `n > 0 ? \`+${n}\` : String(n)` and rounded nothing, while
 * SharedView.tsx:1131 took a `digits` argument defaulting to 0. The same day
 * could therefore print as "-1.33" on a card and "-1" in the table three
 * centimetres below it — the app disagreeing with itself about a number, which
 * is the defect `moodLabel` was moved here to end.
 */
/**
 * HOW A DAY WENT, as one number: where it ended, falling back to its mean.
 *
 * The fallback is only for a payload built before `lastMood` existed — a
 * reader holding a cached page sees what they saw yesterday rather than an
 * empty card.
 *
 * It lived in DayStrip.tsx, with the comment "It is one function so the two
 * call sites cannot drift." There were three. The Log's day heading is in a
 * different file, computed its own day figure from the check-in rows it
 * already held, and took the MEAN — so a day read "−3 Rough" on Today and
 * "A bit low · −1.4 average" in the Log, with nothing on either screen saying
 * which was which. A function cannot stop a drift in a file that does not
 * import it, so it moved here, where both sides reach it.
 *
 * WHY the end and not the mean is at status.ts:125: a mean cannot tell a day
 * that started badly and recovered from one that ended in the low, and those
 * are opposite experiences.
 */
export function dayEndMood(d) {
    return d.lastMood ?? d.meanMood;
}
export function signedFigure(n, digits = 0) {
    const t = n.toFixed(digits);
    return n > 0 ? `+${t}` : t;
}
/**
 * A MEAN, signed, to one decimal, with a trailing zero dropped.
 *
 * Separate from `signedFigure` because a mean and a rating are different
 * things and are shown differently: a rating is a whole number he gave and
 * prints as "+2"; a mean is derived and prints as "-0.7", but "+2.0" would
 * claim a precision the single check-in behind it does not have.
 *
 * FOUR COPIES, and they disagreed. History.tsx, Trends.tsx and
 * LifeEventPage.tsx each rounded to one decimal; DaySummary.tsx — "Today,
 * added up", on his own Home — rounded not at all, so a day with three
 * check-ins showed "-0.67 average" there while the Log showed "-0.7" for the
 * same day. Release 2 fixed the two copies that disagreed on the page he
 * SHARES and left these, which is why the commit claiming "one signed(), not
 * two" was wrong: there were five.
 */
export function signedMean(n) {
    const r = Math.round(n * 10) / 10;
    return `${r > 0 ? '+' : ''}${r}`;
}
export function moodWithWord(mood, digits = 0) {
    return `${moodLabel(mood)} ${signedFigure(mood, digits)}`;
}
/* =============================================================================
 * WHICH OPTIONAL RATINGS ARE ASKED.
 *
 * Here, not in the check-in screen, because the page he SHARES has to name
 * them too: a recipient reading a table with seven columns needs to know that
 * four are still asked and three are history. That sentence is a second
 * statement of this list, and a restatement is never checked against the rule.
 *
 * TURNING A QUESTION OFF NEVER ERASES ITS ANSWERS. The retired columns stay in
 * the database and every value already in them keeps drawing everywhere it
 * draws today. Only the asking stops.
 * ========================================================================== */
/** Asked on the check-in screen, in the order asked. */
export const ASKED_RATINGS = [
    { key: 'energy', label: 'Energy', low: 'Empty', high: 'Full' },
    { key: 'overwhelm', label: 'Overwhelm', low: 'Manageable', high: 'Drowning' },
    { key: 'restlessness', label: 'Restlessness', low: 'Still', high: 'Wired' },
    { key: 'anxiety', label: 'Anxiety', low: 'Calm', high: 'On edge' },
];
/** Asked until 1 October 2026. Their columns and their values stay. */
export const RETIRED_RATINGS = [
    { key: 'focus', label: 'Focus' },
    { key: 'irritability', label: 'Irritability' },
    { key: 'rejectionSensitivity', label: 'Rejection sting' },
];
/** Every rating column, asked or not — what a table still has to render. */
export const ALL_RATINGS = [...ASKED_RATINGS, ...RETIRED_RATINGS];
/**
 * The morning's three words for last night. Release 3 of 2.0.
 *
 * Words, never hours: a number of hours is how sleep becomes a reproach, and
 * this app does not count against the person. `short` is the same fact the
 * `short_night` day mark records; the mark stays, and the morning face sets
 * it when `short` is chosen so nothing that reads marks is left out.
 */
export const DAY_SLEEP = ['short', 'okay', 'long'];
export const daySleep = z.enum(DAY_SLEEP);
/** Sleep from Health: the two boundaries, in hours, that turn a night's minutes into the word. His to move in Settings. */
export const SLEEP_BOUNDS = { shortUnder: 6, longOver: 8 };
export function sleepWordFor(minutes, bounds = SLEEP_BOUNDS) {
    if (minutes < bounds.shortUnder * 60)
        return 'short';
    if (minutes > bounds.longOver * 60)
        return 'long';
    return 'okay';
}
//# sourceMappingURL=mood.js.map